import { useEffect, useState } from 'react';

import classNames from 'classnames';
import { Transition } from '@headlessui/react';
import { useParams } from 'react-router-dom';
import { ClockIcon, PencilIcon } from '@heroicons/react/outline';

import {
  useProgramFollowUpQuery,
  ProgramActivityDataFragment,
  useMarkCommentsReadMutation,
} from '../../../../generated/graphql';

import { defaultTransitionProps } from '../../../lib/animation';
import {
  formatMailEditedAt,
  formatMailSentAt,
  getWeekdayDate,
} from '../../../lib/time';
import Spinner from '../../../svgs/Spinner';

import ActionButton from '../../../components/ActionButton';
import ActionButtonGroup from '../../../components/ActionButtonGroup';
import AudioPlayer from '../../../components/AudioPlayer';
import Avatar from '../../../components/Avatar';
import { usePatientProfileContext } from '..';
import Chat from '../../../components/Comments/Chat';
import ProgramActivityCard from '../../../components/ProgramActivityCard';
import ProgramActivitySlideover from '../../../components/ProgramActivitySlideover';
import { useAuth } from '../../../../contexts/AuthContext';
import { customToast } from '../../../components/ToastAlert/customToast';
import { isProviderUserCurrentSigner } from '../../../components/ProgramActivityCard/helpers';
import Editor from '../../../components/Editor';
import EditFollowUpMessageModal from './EditFollowUpMessageModal';
import MailActivityModal from './MailActivityModal';

const FollowUpDetails = () => {
  const params = useParams();
  const programFollowUpId = params.programFollowUpId!;

  const { patient } = usePatientProfileContext();
  const { showUpgradeBanner, authedProviderUser } = useAuth();

  const [markCommentsReadMutation] = useMarkCommentsReadMutation();

  const {
    data: programFollowUpData,
    error: programFollowUpError,
    loading: programFollowUpLoading,
    refetch: refetchProgramFollowUp,
  } = useProgramFollowUpQuery({
    variables: {
      programFollowUpId,
    },
    onError: () => {
      customToast.error('Unable to fetch mail.');
    },
  });

  const selectedProgramFollowUp = programFollowUpData?.programFollowUp;

  const noteText = selectedProgramFollowUp?.providerNote;
  const voiceNote = selectedProgramFollowUp?.providerVoiceNote;

  useEffect(() => {
    const markFollowUpCommentsRead = async () => {
      if (
        selectedProgramFollowUp &&
        selectedProgramFollowUp.unreadCommentCount > 0
      ) {
        try {
          await markCommentsReadMutation({
            variables: {
              input: {
                programFollowUpId: selectedProgramFollowUp.id,
              },
            },
            update: (cache, { data }) => {
              cache.modify({
                id: cache.identify(selectedProgramFollowUp),
                fields: {
                  unreadCommentCount: () => 0,
                },
              });
            },
          });
        } catch {
          // Non-blocking: failing to mark comments read should not
          // surface as an unhandled promise rejection.
        }
      }
    };

    markFollowUpCommentsRead();
  }, [selectedProgramFollowUp]);

  const [selectedProgramActivity, setSelectedProgramActivity] =
    useState<ProgramActivityDataFragment>();
  const [isProgramActivitySlideoverOpen, setIsProgramActivitySlideoverOpen] =
    useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);

  return (
    <>
      <Transition
        show={Boolean(programFollowUpData && !programFollowUpError)}
        {...defaultTransitionProps}
        className="h-full overflow-y-auto xl:flex-grow"
      >
        {selectedProgramFollowUp && (
          <div className="flex h-full w-full flex-row items-start justify-start sm:flex-col xl:h-full xl:flex-row">
            <div className="flex h-full w-full flex-grow flex-col overflow-hidden text-neutral-150 shadow-none">
              <div className="flex h-full w-full flex-col overflow-y-auto overscroll-y-contain xl:flex-row">
                {/* Mail details */}
                <div className="flex w-full flex-col p-6 pb-12 xl:overflow-y-auto xl:overscroll-y-contain">
                  {/* Subject and action bar row */}
                  <div className="mb-6 flex w-full flex-row items-start justify-between gap-x-6">
                    <div className="flex flex-1 flex-row items-center gap-x-2">
                      <div className="font-serif text-subtitle-small font-light line-clamp-3">
                        {selectedProgramFollowUp.subject ||
                          getWeekdayDate(
                            selectedProgramFollowUp.createdAt,
                            true,
                          )}
                      </div>
                    </div>
                    <div className="flex shrink-0">
                      <ActionButtonGroup size="small">
                        <ActionButton
                          size="small"
                          aria-label="Activity history"
                          title="Activity history"
                          disabled={!selectedProgramFollowUp?.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsActivityModalOpen(true);
                          }}
                        >
                          <ClockIcon className="h-4 w-4 text-neutral-125" />
                        </ActionButton>
                        {selectedProgramFollowUp.canEditMessage && (
                          <ActionButton
                            size="small"
                            aria-label="Edit mail"
                            title="Edit mail"
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsEditModalOpen(true);
                            }}
                          >
                            <PencilIcon className="h-4 w-4 text-neutral-125" />
                          </ActionButton>
                        )}
                      </ActionButtonGroup>
                    </div>
                  </div>

                  {/* Sender and date */}
                  <div className="mb-4 flex flex-row items-center gap-x-2">
                    <Avatar
                      size="medium-plus"
                      imageUrl={selectedProgramFollowUp.sentByProfileImageUrl}
                      name={selectedProgramFollowUp.sentByName ?? ''}
                    />
                    <div className="flex flex-col justify-center gap-1">
                      <div className="text-caption font-bold text-neutral-125">
                        {selectedProgramFollowUp.sentByName}
                      </div>
                      <div className="text-small-caption text-neutral-110">
                        {formatMailSentAt(
                          selectedProgramFollowUp.createdAt,
                          undefined,
                          authedProviderUser?.provider?.timezone,
                        )}
                        {selectedProgramFollowUp.isEdited &&
                          selectedProgramFollowUp.messageEditedAt && (
                            <span className="font-normal italic">
                              {' '}
                              (edited{' '}
                              {formatMailEditedAt(
                                selectedProgramFollowUp.messageEditedAt,
                                selectedProgramFollowUp.createdAt,
                                undefined,
                                authedProviderUser?.provider?.timezone,
                              )}
                              )
                            </span>
                          )}
                      </div>
                    </div>
                  </div>

                  {voiceNote && (
                    <AudioPlayer
                      playerId={voiceNote.id}
                      duration={voiceNote.duration}
                      mediaUrl={voiceNote.media.url}
                      className="mb-4"
                      triggerStop
                    />
                  )}
                  {noteText && (
                    <div className="mb-6">
                      <Editor
                        readOnly={true}
                        initialContentMarkdown={noteText}
                        elKey={`follow-up-note-${selectedProgramFollowUp.id}-${
                          selectedProgramFollowUp.messageEditedAt ??
                          selectedProgramFollowUp.createdAt
                        }`}
                        className="prose prose-sm max-w-none break-words text-caption"
                        containerClassName="w-full"
                      />
                    </div>
                  )}
                  {!noteText && !voiceNote && (
                    <div className="mb-6 flex w-full items-center justify-center whitespace-pre-wrap rounded-lg border border-neutral-75 p-4 text-caption font-medium text-neutral-100">
                      No note provided
                    </div>
                  )}
                  <div className="flex flex-col gap-y-4">
                    {selectedProgramFollowUp.programActivities.map(
                      (programActivity, index) => {
                        const isCurrentSigner = isProviderUserCurrentSigner(
                          programActivity,
                          authedProviderUser?.id,
                        );

                        return (
                          <ProgramActivityCard
                            key={`programActivityCard_${index}`}
                            programActivity={programActivity}
                            className={classNames(
                              index ===
                                selectedProgramFollowUp.programActivities
                                  .length -
                                  1 && 'mb-6',
                            )}
                            onClickProgramActivity={(programActivity) => {
                              setSelectedProgramActivity(programActivity);
                              setIsProgramActivitySlideoverOpen(true);
                            }}
                            authedProviderUserId={authedProviderUser?.id}
                            isCurrentSigner={isCurrentSigner}
                          />
                        );
                      },
                    )}
                  </div>
                </div>

                {/* Comments */}
                <div className="relative h-full w-full flex-grow xl:mb-0 xl:w-1/3 xl:min-w-[400px] xl:border-l xl:border-neutral-75">
                  <Chat
                    chatHeader="Comments"
                    patient={patient}
                    comments={selectedProgramFollowUp.comments}
                    programFollowUpId={selectedProgramFollowUp.id}
                    onCommentAdded={() => refetchProgramFollowUp()}
                    onCommentDeleted={() => refetchProgramFollowUp()}
                    providerUser={authedProviderUser}
                    className={
                      !showUpgradeBanner ? 'max-h-[563px]' : 'max-h-[507px]'
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </Transition>

      <ProgramActivitySlideover
        isOpen={isProgramActivitySlideoverOpen}
        onClose={() => setIsProgramActivitySlideoverOpen(false)}
        programActivity={selectedProgramActivity}
        onVoided={async () => {
          setIsProgramActivitySlideoverOpen(false);
          await refetchProgramFollowUp();
        }}
        authedProviderUserId={authedProviderUser?.id}
        programId={patient?.programInstanceId}
      />

      {selectedProgramFollowUp && (
        <>
          <EditFollowUpMessageModal
            isOpen={isEditModalOpen}
            setClosed={() => setIsEditModalOpen(false)}
            programFollowUp={selectedProgramFollowUp}
            onSuccess={() => refetchProgramFollowUp()}
          />
          <MailActivityModal
            isOpen={isActivityModalOpen}
            setClosed={() => setIsActivityModalOpen(false)}
            programFollowUpId={selectedProgramFollowUp.id}
          />
        </>
      )}

      {programFollowUpLoading && <Spinner className="mx-auto mt-8" />}
    </>
  );
};

export default FollowUpDetails;
